import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronRight, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import useSopState, { ISop } from '@/states/sop.state';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import useTenantState from '@/states/tenant.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import SOPEditModal from './sop/SOPEditModal';
import StepTipsPanel from '@/components/company/StepTipsPanel';

type MergedSopRow = {
  id: number | string;
  _jobDesc: IJobDescription;
  _sop: ISop | null;
};

export default function SOP() {
  const navigate = useNavigate();
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const { currentTenant } = useTenantState();
  const isReadOnly = useIsReadOnly();
  const { sops, getSops, isLoading, createSop, updateSop } = useSopState();
  const { jobDescriptions, paginatedData: jdPaginatedData, getJobDescriptions } = useJobDescriptionState();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const sopStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'sop');
  const videoLink = sopStep?.video_link;
  const tips = sopStep?.tips;

  const [showPanel, setShowPanel] = useState(true);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'org_level',
    order: 'asc',
  });
  const [editTarget, setEditTarget] = useState<{
    jobDesc: IJobDescription;
    sop: ISop | null;
    localSopName: string;
  } | null>(null);

  // Track locally edited SOP names (before saved via modal)
  const [localSopNames, setLocalSopNames] = useState<Record<string | number, string>>({});

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;
    try {
      await Promise.all([
        getJobDescriptions(currentTenant.id, {
          page: pageConfig.page,
          page_size: pageConfig.limit,
          order: pageConfig.order as 'asc' | 'desc',
          order_by: pageConfig.orderBy,
        }),
        getSops(currentTenant.id, {
          page: 0,
          page_size: 100,
        }),
      ]);
    } catch {
      toast.error('Gagal memuat data SOP');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    getJobDescriptions,
    getSops,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mergedRows: MergedSopRow[] = (jobDescriptions ?? []).map(jd => {
    const matchingSop = sops.find(sop => Number(sop.org_job_description_id) === Number(jd.id)) ?? null;
    return { id: jd.id!, _jobDesc: jd, _sop: matchingSop };
  });

  const totalElements = jdPaginatedData?.totalElements ?? 0;
  const totalPages = jdPaginatedData?.totalPages ?? 0;

  const handleSaveAndNext = async () => {
    if (!currentTenant?.id) return;

    try {
      // Save all locally edited SOP names
      for (const row of mergedRows) {
        const localName = localSopNames[row.id];
        if (localName && localName !== (row._sop?.name ?? '')) {
          // Require SOP details (4M configuration) to exist
          const sopDetails = row._sop?.sop ?? row._sop?.org_sop ?? [];
          if (sopDetails.length === 0) {
            toast.error(`${localName}: Wajib mengonfigurasi 4M sebelum menyimpan`);
            return;
          }

          const payload = {
            name: localName,
            org_chart_note_id: row._jobDesc.org_chart_node_id,
            org_job_description_id: row._jobDesc.id,
            is_published: row._sop?.is_published ?? false,
            sop: sopDetails,
          };

          if (row._sop?.id) {
            await updateSop(currentTenant.id, row._sop.id, payload);
          } else if (localName.trim()) {
            await createSop(currentTenant.id, payload);
          }
        }
      }
      await refetchCompletionStatus();
    } catch {
      toast.error('Gagal menyimpan SOP');
      return;
    }

    navigate(`/${tenant_id}/train`);
  };

  const thClass =
    'px-4 py-3 text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide text-left border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800';

  return (
    <section className="w-full">
      {/* Page header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Standar Operasional Prosedur (SOP)</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Menyusun SOP menggunakan metode 4M yang mudah: (Metode, Manusia, Mesin, Material).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          <button
            type="button"
            onClick={handleSaveAndNext}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium"
            style={{ background: '#FD6118' }}
          >
            Simpan &amp; Lanjut 🚀
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {/* Table */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed min-w-[650px] xl:min-w-[800px] text-sm border-collapse">
                <thead>
                  <tr>
                    <th className={`${thClass} w-[250px] sticky left-0 z-10`}>Jabatan</th>
                    <th className={`${thClass} w-[350px]`}>Uraian Tugas</th>
                    <th className={`${thClass} w-[300px] !text-center`}>
                      Nama SOP <span className="text-red-400">*</span>
                    </th>
                    <th className={`${thClass} w-[200px] !text-center`}>
                      Aksi <span className="text-red-400">*</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                        Memuat data...
                      </td>
                    </tr>
                  ) : mergedRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                        Belum ada data.
                      </td>
                    </tr>
                  ) : (
                    mergedRows.map(row => (
                      <SopTableRow
                        key={row.id}
                        row={row}
                        isReadOnly={isReadOnly}
                        localSopName={localSopNames[row.id] ?? null}
                        onLocalNameChange={name => setLocalSopNames(prev => ({ ...prev, [row.id]: name }))}
                        onConfigureClick={displayName =>
                          setEditTarget({ jobDesc: row._jobDesc, sop: row._sop, localSopName: displayName })
                        }
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                <span>
                  Menampilkan {pageConfig.page * pageConfig.limit + 1}–
                  {Math.min((pageConfig.page + 1) * pageConfig.limit, totalElements)} dari {totalElements} data
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPageConfig(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pageConfig.page === 0}
                    className="p-1.5 rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPageConfig(prev => ({ ...prev, page: i }))}
                      className={`w-7 h-7 rounded-md text-xs font-medium transition-colors ${
                        pageConfig.page === i
                          ? 'bg-primary text-white'
                          : 'border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPageConfig(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pageConfig.page >= totalPages - 1}
                    className="p-1.5 rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tips panel */}
        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editTarget && (
        <SOPEditModal
          jobDesc={editTarget.jobDesc}
          existingSop={editTarget.sop}
          localSopName={editTarget.localSopName}
          tenantId={currentTenant?.id ?? ''}
          isReadOnly={isReadOnly}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            await fetchData();
            await refetchCompletionStatus();
          }}
        />
      )}
    </section>
  );
}

interface SopTableRowProps {
  row: MergedSopRow;
  isReadOnly: boolean;
  localSopName: string | null;
  onLocalNameChange: (name: string) => void;
  onConfigureClick: (displayName: string) => void;
}

function SopTableRow({ row, isReadOnly, localSopName, onLocalNameChange, onConfigureClick }: SopTableRowProps) {
  const savedName = row._sop?.name ?? '';
  const rowCount = (row._sop?.sop ?? row._sop?.org_sop ?? []).length;
  const displayName = localSopName !== null ? localSopName : savedName;

  return (
    <tr className="group border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/40 dark:hover:bg-gray-800/40 align-top">
      <td className="px-4 py-4 font-bold text-gray-800 dark:text-white sticky left-0 bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 z-10">
        <div className="w-full break-words whitespace-normal">{row._jobDesc.org_chart_node?.name ?? '-'}</div>
      </td>

      {/* Uraian Tugas */}
      <td className="px-4 py-4 text-gray-700 dark:text-gray-200">
        <div className="w-full break-words whitespace-normal">{row._jobDesc.name ?? '-'}</div>
      </td>

      {/* Nama SOP — inline editable textarea */}
      <td className="px-3 py-3">
        {isReadOnly ? (
          <span className="text-sm text-gray-700 dark:text-gray-200">{savedName || '-'}</span>
        ) : (
          <textarea
            value={displayName}
            onChange={e => onLocalNameChange(e.target.value)}
            rows={2}
            maxLength={150}
            placeholder="Contoh: SOP Rekonsiliasi Bank"
            className={`w-full text-sm px-2 py-1.5 border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500 ${
              displayName
                ? 'border-blue-400 dark:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-300'
            }`}
          />
        )}
      </td>

      {/* Konfigurasi 4M */}
      <td className="px-4 py-4 text-center">
        <button
          type="button"
          onClick={() => onConfigureClick(displayName)}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
        >
          {rowCount > 0 ? `Kelola SOP (${rowCount} Baris)` : 'Kelola SOP'}
        </button>
      </td>
    </tr>
  );
}
